import { useState, useEffect, useCallback, useRef } from 'react';
import { createClient } from '@wix/sdk';
import { reactElements, info } from '@wix/editor';
import type { Host } from '@wix/sdk-types';
import type { StylesDerivedNativeActionNames } from '@wix/editor-platform-host-integration-apis';
import { GlobalDesignStyleEngine } from './engine';
import { StyleMetadataEngine } from './StyleMetadataEngine';
import type { AggregatedStyleValues, StyleSDKClient } from './types';
import { ComponentsPlatformService } from '@wix/editor-platform-manifest-services';

export interface UseGlobalDesignStylesConfig {
  /**
   * If true, resolves CSS variables using the components API.
   * Default: false
   */
  withVariablesResolution?: boolean;

  /**
   * If true, performs additional SDK refetch after updates for consistency.
   * Default: false
   */
  shouldRefetchOnUpdate?: boolean;

  /**
   * Optional pre-existing StyleMetadataEngine from group.
   * If provided, engine will be transferred (no redundant SDK calls).
   * Added in Phase 4.
   */
  baseEngine?: StyleMetadataEngine;
}

export interface UseGlobalDesignStylesResult {
  /**
   * Aggregated style values (raw, decomposed, metadata, definitions).
   * Null during initial load.
   */
  values: AggregatedStyleValues | null;

  /**
   * Loading state. True during initialization.
   */
  isLoading: boolean;

  /**
   * Error state. Contains error if initialization or update failed.
   */
  error: Error | null;

  /**
   * SDK client.
   */
  sdkClient: ReturnType<typeof createClient> | null;

  /**
   * Updates style properties with smart propagation.
   */
  updateStyles: (changes: Record<string, string>) => Promise<void>;

  /**
   * Sets style properties directly without smart propagation.
   */
  setStyles: (changes: Record<string, string>) => Promise<void>;

  /**
   * Manually refetch all values from the SDK.
   */
  refetch: () => Promise<void>;
}

/**
 * React hook for Global Design styles.
 * Thin wrapper around GlobalDesignStyleEngine - delegates all logic to engine.
 */
export function useGlobalDesignStyles(
  host: Host,
  actionName: StylesDerivedNativeActionNames,
  componentsAPI: ComponentsPlatformService,
  config: UseGlobalDesignStylesConfig = {},
): UseGlobalDesignStylesResult {
  const {
    shouldRefetchOnUpdate = false,
    withVariablesResolution = false,
    baseEngine,
  } = config;

  // React state
  const [values, setValues] = useState<AggregatedStyleValues | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [sdkClient, setSdkClient] = useState<ReturnType<
    typeof createClient
  > | null>(null);

  // Engine ref (stable across renders)
  const engineRef = useRef<GlobalDesignStyleEngine | null>(null);

  // Initialize engine
  useEffect(() => {
    (async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Create SDK client
        const client = createClient({
          host,
          modules: { element: reactElements, reactElements, info },
        });
        setSdkClient(client);
        const sdkClient: StyleSDKClient = {
          getStyleDefinitions: () => client.element.getStyleDefinitions(),
          getStyles: () => client.element.getStyles(),
          setStyles: (styles) => client.element.setStyles(styles),
        };

        // Create engine (with optional transfer)
        const engine = baseEngine
          ? GlobalDesignStyleEngine.fromMetadataEngine(
              baseEngine,
              actionName,
              sdkClient,
              componentsAPI,
              { shouldRefetchAfterUpdate: shouldRefetchOnUpdate },
            )
          : new GlobalDesignStyleEngine(actionName, sdkClient, componentsAPI, {
              shouldRefetchAfterUpdate: shouldRefetchOnUpdate,
              withVariablesResolution,
            });

        engineRef.current = engine;

        // Initialize engine (or use already-initialized values from transfer)
        const existingValues = engine.getCurrentValues();
        if (existingValues) {
          // Engine already initialized (from transfer)
          setValues(existingValues);
          setIsLoading(false);
        } else {
          // Need to initialize
          const initialValues = await engine.initialize();
          setValues(initialValues);
          setIsLoading(false);
        }
      } catch (err) {
        const initError = err instanceof Error ? err : new Error('Init failed');
        setError(initError);
        setIsLoading(false);
        console.error('[useGlobalDesignStyles] Init failed:', initError);
      }
    })();

    // Cleanup
    return () => {
      engineRef.current?.cancel();
    };
  }, [host, actionName, baseEngine, shouldRefetchOnUpdate]);

  // Delegate to engine (stable callbacks)
  const updateStyles = useCallback(async (changes: Record<string, string>) => {
    if (!engineRef.current) {
      throw new Error('Engine not initialized');
    }
    try {
      const newValues = await engineRef.current.updateProperties(changes);
      setValues(newValues);
      setError(null);
    } catch (err) {
      const updateError =
        err instanceof Error ? err : new Error('Update failed');
      setError(updateError);
      throw updateError;
    }
  }, []);

  const setStyles = useCallback(async (changes: Record<string, string>) => {
    if (!engineRef.current) {
      throw new Error('Engine not initialized');
    }
    try {
      const newValues = await engineRef.current.setPropertiesDirectly(changes);
      setValues(newValues);
      setError(null);
    } catch (err) {
      const updateError = err instanceof Error ? err : new Error('Set failed');
      setError(updateError);
      throw updateError;
    }
  }, []);

  const refetch = useCallback(async () => {
    if (!engineRef.current) {
      throw new Error('Engine not initialized');
    }
    setIsLoading(true);
    try {
      const newValues = await engineRef.current.refetch();
      setValues(newValues);
      setError(null);
    } catch (err) {
      const refetchError =
        err instanceof Error ? err : new Error('Refetch failed');
      setError(refetchError);
      throw refetchError;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    values,
    isLoading,
    sdkClient,
    error,
    updateStyles,
    setStyles,
    refetch,
  };
}
